import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";
import { cannesEventPriceFaq, cannesEventSeo } from "./eventFaq";

const mipim: ChauffeurPage = {
  slug: "mipim",
  title: "Chauffeur privé MIPIM Cannes – VTC affaires",
  subtitle:
    "Chauffeur privé pendant le MIPIM : transferts dirigeants, rendez-vous multi-stops et dîners clients autour du Palais des Festivals.",
  keywords: ["chauffeur mipim", "vtc mipim cannes", "chauffeur privé mipim"],
  hero: {
    title: "Chauffeur privé pendant le MIPIM",
    subtitle:
      "Plus grand salon immobilier mondial, en mars à Cannes : un chauffeur d'affaires pour tenir un agenda B2B dense.",
    keywords: [
      "chauffeur MIPIM",
      "VTC affaires Cannes",
      "chauffeur Palais des Festivals",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant le MIPIM",
    badges: [
      "Transferts dirigeants",
      "Rendez-vous multi-stops",
      "Dîners clients",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur d'affaires au MIPIM",
    items: [
      {
        title: "Agenda B2B tenu",
        description:
          "Stands au Palais, pavillons, déjeuners et rendez-vous s'enchaînent : un chauffeur dédié garde le tempo sans temps mort.",
      },
      {
        title: "Roadshows multi-stops",
        description:
          "Plusieurs rendez-vous clients dans la journée, à Cannes et alentours, avec le même véhicule et le même chauffeur.",
      },
      {
        title: "Transferts aéroport business",
        description:
          "Nice Côte d'Azur en pic de mars : suivi de vol, accueil pancarte et liaison directe vers les hôtels de la Croisette.",
      },
      {
        title: "Dîners & networking",
        description:
          "Soirées clients, dîners de prospection et réceptions partenaires, retour de nuit assuré.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant le MIPIM",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison Nice ↔ Cannes avec suivi de vol pendant le pic d'arrivées du salon en mars.",
      },
      {
        title: "Mise à disposition journée",
        text: "Chauffeur dédié à l'heure ou à la journée pour enchaîner rendez-vous au Palais et déjeuners d'affaires.",
      },
      {
        title: "Roadshow clients",
        text: "Itinéraires multi-stops optimisés entre stands, pavillons, bureaux et hôtels partenaires.",
      },
      {
        title: "Soirées & dîners",
        text: "Réceptions de networking, dîners de prospection et soirées sur la Croisette, retour tardif garanti.",
      },
      {
        title: "Navettes délégations",
        text: "Coordination multi-véhicules pour équipes commerciales et délégations institutionnelles.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant le MIPIM",
    items: [
      {
        name: "Palais des Festivals et des Congrès",
        description: "Stands, pavillons nationaux et espaces de rendez-vous.",
      },
      {
        name: "La Croisette",
        description:
          "Hôtels d'affaires, terrasses et plages privatisées par les exposants.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description: "Terminaux 1 et 2, arrivées affaires concentrées en mars.",
      },
      {
        name: "Sophia Antipolis / arrière-pays",
        description: "Bureaux, sièges et rendez-vous hors centre.",
      },
      {
        name: "Mougins & Mandelieu",
        description: "Dîners clients et hébergements des délégations.",
      },
    ],
  },
  generic: {
    heading: "Le MIPIM vu d'un chauffeur d'affaires",
    subheading:
      "En mars, le plus grand salon immobilier mondial densifie Cannes de rendez-vous B2B back-to-back.",
    paragraphs: [
      "Chaque mois de mars, le MIPIM réunit au Palais des Festivals investisseurs, promoteurs et collectivités du monde entier. L'agenda est dense : stands, pavillons, déjeuners et rendez-vous s'enchaînent sur la Croisette et un retard de transport peut coûter une réunion.",
      "Les arrivées business se concentrent à l'aéroport de Nice Côte d'Azur. Suivi de vol, accueil pancarte et transfert direct vers l'hôtel évitent l'attente quand le salon fait grimper la demande.",
      "Au-delà du transfert, la mise à disposition transforme la journée en roadshow fluide : plusieurs rendez-vous clients, des déjeuners et des dîners de networking enchaînés avec le même chauffeur, jusqu'aux soirées tardives.",
    ],
    bullets: [
      "Roadshow multi-stops",
      "Suivi de vol en mars",
      "Dîners de prospection",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour le MIPIM",
    steps: [
      "Communiquez vos dates de salon, votre hôtel et la liste de vos rendez-vous.",
      "Nous construisons l'itinéraire multi-stops et calons les horaires sur votre agenda.",
      "Le chauffeur se présente en tenue, avec suivi de vol pour vos arrivées à Nice.",
      "Mise à disposition journée et soirée pour tenir réunions, déjeuners et dîners.",
    ],
  },
  faq: [
    ...cannesEventPriceFaq("fr", "pendant le MIPIM"),
    {
      q: "Comment réserver un chauffeur d'affaires pendant le MIPIM ?",
      a: "Réservez en amont : début mars, la demande sur Cannes grimpe fortement. Transmettez vos dates, votre hôtel et vos rendez-vous pour que nous bloquions un chauffeur dédié et préparions un itinéraire multi-stops.",
    },
    {
      q: "Pouvez-vous enchaîner plusieurs rendez-vous clients dans la journée ?",
      a: "Oui, c'est l'usage typique du MIPIM. La mise à disposition garde le même chauffeur et le même véhicule pour un roadshow complet entre stands, bureaux et hôtels, sans réserver à chaque trajet.",
    },
    {
      q: "Gérez-vous les arrivées à l'aéroport de Nice pendant le salon ?",
      a: "Oui, avec suivi de vol et accueil pancarte. En mars, l'aéroport de Nice Côte d'Azur connaît un pic d'arrivées business ; nous anticipons et adaptons l'heure de prise en charge en cas de retard.",
    },
    {
      q: "Assurez-vous les dîners et soirées de networking ?",
      a: "Oui. Les dîners de prospection et réceptions partenaires se terminent tard : le chauffeur patiente et garantit votre retour de nuit vers l'hôtel.",
    },
  ],
  relatedLinks: [
    {
      label: "Réserver un transfert Cannes Aéroport de Nice",
      href: "/chauffeur/transferts/cannes-aeroport-nice",
    },
    {
      label: "Réserver un transfert Cannes Monaco",
      href: "/chauffeur/transferts/cannes-monaco",
    },
    {
      label: "Vérifier les disponibilités",
      href: "/reservation",
    },
  ],
  cta: {
    title: "Votre chauffeur d'affaires pour le MIPIM",
    text: "Transferts aéroport, roadshows clients et dîners : réservez un chauffeur dédié pour le salon.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    // MESURE 2026-10-09 : page événement sans prix, même recette que le Festival.
    ...cannesEventSeo("fr", "MIPIM Cannes", "Rendez-vous clients en chaîne."),
    canonical: "/chauffeur/mipim",
  },
};

export default mipim;
